import React from 'react';
import './Avatar.css';

interface AvatarProps {
  name: string;
  size?: 'small' | 'medium' | 'large';
  isOnline?: boolean;
  showOnlineStatus?: boolean;
}

/**
 * Generates a consistent color based on the name string
 */
const getAvatarColor = (name: string): { from: string; to: string } => {
  const colors = [
    { from: '#1D4ED8', to: '#9333EA' }, // Blue to Purple
    { from: '#10B981', to: '#059669' }, // Green shades
    { from: '#F59E0B', to: '#D97706' }, // Amber shades
    { from: '#EF4444', to: '#DC2626' }, // Red shades
    { from: '#8B5CF6', to: '#7C3AED' }, // Violet shades
    { from: '#EC4899', to: '#DB2777' }, // Pink shades
    { from: '#06B6D4', to: '#0891B2' }, // Cyan shades
    { from: '#F97316', to: '#EA580C' }, // Orange shades
  ];

  // Generate a hash from the name
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  const index = Math.abs(hash) % colors.length;
  return colors[index];
};

/**
 * Extracts initials from a name or email
 */
const getInitials = (name: string): string => {
  if (!name) return '?';
  
  // If it's an email, use the part before @
  const displayName = name.includes('@') ? name.split('@')[0] : name;
  
  // Split by common separators
  const parts = displayName.split(/[\s._-]+/).filter(Boolean);
  
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  
  if (parts.length === 1 && parts[0].length >= 2) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  
  return displayName.substring(0, 2).toUpperCase();
};

const Avatar: React.FC<AvatarProps> = ({
  name,
  size = 'medium',
  isOnline = false,
  showOnlineStatus = false,
}) => {
  const colors = getAvatarColor(name);
  const initials = getInitials(name);
  
  const gradientStyle = {
    background: `linear-gradient(135deg, ${colors.from}, ${colors.to})`,
  };

  return (
    <div className={`avatar avatar--${size}`} style={gradientStyle}>
      <span className="avatar__initials">{initials}</span>
      {showOnlineStatus && (
        <span 
          className={`avatar__status ${isOnline ? 'avatar__status--online' : 'avatar__status--offline'}`}
          title={isOnline ? 'Online' : 'Offline'}
        />
      )}
    </div>
  );
};

export default Avatar;
